/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import {
  AuthAccessTokenDetails,
  DecodedToken,
  OauthAEMConfig
} from '../../../../models/login';
import { LoginRememberMeUtilsService } from '../../../../utils/login';
import { LoggingService } from '../../../utils';
import { LoginRememberMeWrapperService } from '../../wrappers/login-remember-me-wrapper.service';
import { UserDataService } from '../user-session/user-data/user-data.service';

@Injectable({
  providedIn: 'root'
})
export class AppTokenHelperService {
  protected loginRememberMeWrapper = inject(LoginRememberMeWrapperService);
  protected loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  protected logging = inject(LoggingService);
  protected userData = inject(UserDataService);

  tokenMap: Map<string, AuthAccessTokenDetails> = new Map();

  getExistingToken(config: OauthAEMConfig): AuthAccessTokenDetails {
    return (
      this.tokenMap.get(config.CLIENT_ID_VALUE) ||
      ({} as AuthAccessTokenDetails)
    );
  }

  isTokenValid(config: OauthAEMConfig, token: AuthAccessTokenDetails): boolean {
    const refreshTokenTime = config?.REFRESH_TOKEN_TIME
      ? config.REFRESH_TOKEN_TIME
      : 300000;
    return (
      this.validateClientId(config, token) &&
      !(this.checkTokenExpiryTime(token) <= refreshTokenTime)
    );
  }

  validateClientId(
    config: OauthAEMConfig,
    token: AuthAccessTokenDetails
  ): boolean {
    const decodedToken: DecodedToken = jwtDecode(token.access_token);
    if (decodedToken && decodedToken.client_id === config.CLIENT_ID_VALUE) {
      return true;
    } else {
      return false;
    }
  }

  checkTokenExpiryTime(token: AuthAccessTokenDetails): number {
    const tokenValidityTime =
      token.token_issued_time +
      (token?.expires_in ? token.expires_in : 1799) * 1000;
    return tokenValidityTime - new Date().getTime();
  }

  sendHeaderTokenServiceWorker(
    config: OauthAEMConfig,
    tokenResponse: AuthAccessTokenDetails
  ): void {
    if (
      config?.ENABLE_NEW_PERSISTENT_LOGIN &&
      this.loginRememberMeUtils.checkServiceWorkerController() &&
      config.CLIENT_ID_VALUE === config.HEADER_CLIENT_ID_VALUE
    ) {
      this.loginRememberMeUtils.sendMessage({
        type: 'ADD_HEADER_TOKEN',
        headerToken: tokenResponse?.access_token,
        headerIssueTime: tokenResponse?.token_issued_time,
        headerExpiresIn: tokenResponse?.expires_in
      });
    }
  }

  async isHeaderTokenAvailable(): Promise<AuthAccessTokenDetails> {
    if (this.loginRememberMeUtils.checkServiceWorkerController()) {
      return await this.loginRememberMeUtils
        .sendMessage({ type: 'GET_HEADER_TOKEN' })
        .then(async (data: any) => {
          if (
            data?.headerToken &&
            data?.headerIssueTime &&
            data?.headerExpiresIn
          ) {
            return {
              access_token: data?.headerToken,
              token_issued_time: data?.headerIssueTime,
              expires_in: data?.headerExpiresIn
            } as AuthAccessTokenDetails;
          } else {
            return {} as AuthAccessTokenDetails;
          }
        })
        .catch(() => {
          return {} as AuthAccessTokenDetails;
        });
    } else {
      return {} as AuthAccessTokenDetails;
    }
  }
}
